import React, { useState, useEffect, lazy, Suspense } from "react";
const SkipperDashboard = lazy(() => import("./components/SkipperDashboard"));
import PublicReport from "./components/PublicReport";
import SailingLoader from "./components/SailingLoader";
import { loadRaces, saveRace, deleteRace, loadSignups, addSignup, removeSignup, loadNotRacing, setNotRacing, loadBoatFull, setBoatFull, loadReactions, addReaction, removeReaction, loadComments, addComment, updateComment, removeComment, appendRacePhotos, loadAnnouncement, setAnnouncement, clearAnnouncement, loadBoatInfo, saveBoatInfo, loadSeries } from "./supabase";

const BAKED_RACES = [
  {
    id: "2026-06-04", raceNo: 1, date: "2026-06-04",
    title: "Gusty, shifty, top-half", raceClass: "Racing",
    place: 6, fleet: 11, time: "0:58:01", winner: "Meridian, 0:43:51",
    twd: "163° SSE (σ 81°)", maxSog: "7.8", avgSog: "5.1", avgTws: "7.0", maxTws: "22.5",
    maxPolar: "84", avgPolar: "9",
    crew: ["Frederick", "Jules", "Eli", "Michele"], photos: [],
    story: "A shifty southerly evening — 7 knots average true wind but gusting to 22.5, with the direction swinging through more than 80 degrees over the race. Pardon My French handled the chaos well, finishing 6th of 11 at 58:01, solidly in the top half of the fleet. We touched 7.8 knots over ground when the pressure came on and averaged 5.1 through the evening. Meridian took the gun in 43:51."
  },
  {
    id: "2026-06-11", raceNo: 2, date: "2026-06-11",
    title: "Big fleet, light air", raceClass: "Racing",
    place: 11, fleet: 17, time: "1:01:05", winner: "Mayhem, 0:35:27",
    twd: "207° SSW (σ 71°)", maxSog: "7.5", avgSog: "4.9", avgTws: "5.5", maxTws: "20.3",
    maxPolar: "82", avgPolar: "11",
    crew: ["Frederick", "Dominik", "Eli", "Eric", "Cosie", "+1"],
    photos: [],
    story: "Bigger fleet, lighter air. Seventeen boats turned out and the breeze averaged just 5.5 knots out of the SSW — a night that rewarded the lighter boats up front, with Mayhem running away with it in 35:27. PMF crossed 11th at 1:01:05. A max SOG of 7.5 knots shows we found pressure when it was there; the 4.9-knot average tells the story of the holes in between. Two races in, the pattern is clear: PMF likes wind. Hotdogs earned either way."
  }
];

export const SCHEDULE = [
  { raceNo: 1,  date: "2026-06-04" }, { raceNo: 2,  date: "2026-06-11" },
  { raceNo: 3,  date: "2026-06-18" }, { raceNo: 4,  date: "2026-06-25" },
  { raceNo: 5,  date: "2026-07-02" }, { raceNo: 6,  date: "2026-07-09" },
  { raceNo: 7,  date: "2026-07-16" }, { raceNo: 8,  date: "2026-07-23" },
  { raceNo: 9,  date: "2026-07-30" }, { raceNo: 10, date: "2026-08-06" },
  { raceNo: 11, date: "2026-08-13" }
];

export const ICLOUD_ALBUM = "https://www.icloud.com/sharedalbum/#B1x5ZhN2vZ3njQ";
export const BOAT_NUMBER = "2590";
export const SKIPPER_PIN = "2590";
export const ELLIOTT_BAY = { lat: 47.6276, lon: -122.3970 }; // Elliott Bay Marina

export default function App() {
  const [races, setRaces]     = useState([]);
  const [signups, setSignups] = useState([]);
  const [notRacing, setNotRacingState] = useState([]);
  const [boatFull, setBoatFullState] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [comments, setComments] = useState([]);
  const [announcement, setAnnouncementState] = useState(null);
  const [boatInfo, setBoatInfoState] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skipperMode, setSkipperMode] = useState(false);
  const [pinPrompt, setPinPrompt] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState(false);

  const tryEnterSkipper = () => { setPinPrompt(true); setPinInput(""); setPinError(false); };

  const submitPin = () => {
    if (pinInput === SKIPPER_PIN) {
      setSkipperMode(true);
      setPinPrompt(false);
      setPinInput("");
      setPinError(false);
    } else {
      setPinError(true);
      setPinInput("");
    }
  };

  const refresh = async () => {
    const [r, s, nr, bf, rx, cm, ann, bi] = await Promise.all([loadRaces(), loadSignups(), loadNotRacing().catch(() => []), loadBoatFull().catch(() => []), loadReactions().catch(() => []), loadComments().catch(() => []), loadAnnouncement().catch(() => null), loadBoatInfo().catch(() => null)]);
    setRaces(r.length > 0 ? r : BAKED_RACES);
    setSignups(s);
    setNotRacingState(nr);
    setBoatFullState(bf);
    setReactions(rx);
    setComments(cm);
    setAnnouncementState(ann);
    setBoatInfoState(bi);
    loadSeries().then(setSeries).catch(() => {});
  };

  useEffect(() => {
    // Load races FIRST so the page renders fast (especially for shared deep-links).
    loadRaces()
      .then(async (data) => {
        if (data.length > 0) {
          setRaces(data);
        } else {
          setRaces(BAKED_RACES);
          Promise.all(BAKED_RACES.map(r => saveRace(r))).catch(console.warn);
        }
      })
      .catch(() => setRaces(BAKED_RACES))
      .finally(() => setLoading(false));

    // Load the rest in the background; the UI fills in as these resolve.
    loadSignups().then(setSignups).catch(() => {});
    loadNotRacing().then(setNotRacingState).catch(() => {});
    loadBoatFull().then(setBoatFullState).catch(() => {});
    loadReactions().then(setReactions).catch(() => {});
    loadComments().then(setComments).catch(() => {});
    loadAnnouncement().then(setAnnouncementState).catch(() => {});
    loadBoatInfo().then(setBoatInfoState).catch(() => {});
    loadSeries().then(setSeries).catch(() => {});
  }, []);

  // Deep-link: once content has loaded, keep the hash-linked race (#race-N) in
  // view. We poll on an interval because cards mount and then shift as weather,
  // temps, and photos load in below them; each tick re-aligns to the element's
  // live position until things settle.
  useEffect(() => {
    if (loading || skipperMode) return;
    // Target id from the hash (#race-N) OR the path (/race/N) — the shared
    // preview link is /race/N and may land here directly.
    let id = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : "";
    if (!id) {
      const m = window.location.pathname.match(/\/race\/(\d+)/);
      if (m) id = "race-" + m[1];
    }
    if (!id) return;

    let ticks = 0;
    let lastTop = null;
    let stable = 0;

    const interval = setInterval(() => {
      ticks += 1;
      const el = document.getElementById(id);
      if (el) {
        const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
        // Re-scroll if the element's absolute position changed since last tick.
        if (top !== lastTop) {
          window.scrollTo({ top, behavior: "auto" });
          lastTop = top;
          stable = 0;
        } else {
          stable += 1;
        }
        // Stop once the position has held steady for several ticks (layout settled)…
        if (stable >= 6) clearInterval(interval);
      }
      // …or give up after ~6 seconds regardless.
      if (ticks > 40) clearInterval(interval);
    }, 150);

    return () => clearInterval(interval);
  }, [loading, skipperMode]);

  const handleSaveRace = async (race) => {
    await saveRace(race);
    await refresh();
  };

  const handleDeleteRace = async (id) => {
    await deleteRace(id);
    await refresh();
  };

  const handleToggleNotRacing = async (raceDate, value) => {
    await setNotRacing(raceDate, value);
    const nr = await loadNotRacing();
    setNotRacingState(nr);
  };

  const handleToggleBoatFull = async (raceDate, value) => {
    await setBoatFull(raceDate, value);
    const bf = await loadBoatFull();
    setBoatFullState(bf);
  };

  const handleSignup = async (raceDate, name) => {
    await addSignup(raceDate, name);
    const s = await loadSignups();
    setSignups(s);
  };

  const handleRemoveSignup = async (id) => {
    await removeSignup(id);
    const s = await loadSignups();
    setSignups(s);
  };

  const handleToggleReaction = async (raceDate, emoji, name, existingId) => {
    if (existingId) await removeReaction(existingId);
    else await addReaction(raceDate, emoji, name);
    const rx = await loadReactions();
    setReactions(rx);
  };

  const handleAddComment = async (raceDate, name, text) => {
    const created = await addComment(raceDate, name, text);
    const cm = await loadComments();
    setComments(cm);
    return created;
  };

  const handleUpdateComment = async (id, text) => {
    await updateComment(id, text);
    const cm = await loadComments();
    setComments(cm);
  };

  const handleCrewPhotos = async (raceId, urls) => {
    await appendRacePhotos(raceId, urls);
    const r = await loadRaces();
    setRaces(r.length > 0 ? r : BAKED_RACES);
  };

  const handleSetAnnouncement = async (message, raceDate) => {
    await setAnnouncement(message, raceDate);
    const ann = await loadAnnouncement().catch(() => null);
    setAnnouncementState(ann);
  };

  const handleClearAnnouncement = async () => {
    await clearAnnouncement();
    const ann = await loadAnnouncement().catch(() => null);
    setAnnouncementState(ann);
  };

  const handleSaveBoatInfo = async (fields) => {
    await saveBoatInfo(fields);
    const bi = await loadBoatInfo().catch(() => null);
    setBoatInfoState(bi);
  };

  const handleRemoveComment = async (id) => {
    await removeComment(id);
    const cm = await loadComments();
    setComments(cm);
  };

  if (loading) return <SailingLoader />;

  // Phase 1b: the season's schedule + gun time now come from the active series
  // (falls back to the baked SCHEDULE until the series loads). No visible change.
  const activeSeries = series[0] || null;
  const schedule = (activeSeries && Array.isArray(activeSeries.schedule) && activeSeries.schedule.length)
    ? activeSeries.schedule.map((date, i) => ({ raceNo: i + 1, date }))
    : SCHEDULE;
  const gunTime = (activeSeries && activeSeries.gun_time) || "19:00";

  return (
    <div>
      {skipperMode
        ? <Suspense fallback={<SailingLoader />}>
            <SkipperDashboard
            races={races}
            schedule={schedule}
            gunTime={gunTime}
            signups={signups}
            notRacing={notRacing}
            boatFull={boatFull}
            comments={comments}
            onRemoveComment={handleRemoveComment}
            onSaveRace={handleSaveRace}
            onDeleteRace={handleDeleteRace}
            onRemoveSignup={handleRemoveSignup}
            onToggleNotRacing={handleToggleNotRacing}
            onToggleBoatFull={handleToggleBoatFull}
            onExit={() => setSkipperMode(false)}
            announcement={announcement}
            onSetAnnouncement={handleSetAnnouncement}
            onClearAnnouncement={handleClearAnnouncement}
            boatInfo={boatInfo}
            onSaveBoatInfo={handleSaveBoatInfo}
          />
          </Suspense>
        : <PublicReport
            races={races}
            schedule={schedule}
            signups={signups}
            notRacing={notRacing}
            boatFull={boatFull}
            reactions={reactions}
            comments={comments}
            onSignup={handleSignup}
            onRemoveSignup={handleRemoveSignup}
            onToggleReaction={handleToggleReaction}
            onAddComment={handleAddComment}
            onEnterSkipper={tryEnterSkipper}
            onUpdateComment={handleUpdateComment}
            onRemoveComment={handleRemoveComment}
            onCrewPhotos={handleCrewPhotos}
            announcement={announcement}
            boatInfo={boatInfo}
          />
      }

      {/* PIN gate modal */}
      {pinPrompt && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ background:"#fff", borderRadius:12, padding:"32px 28px", maxWidth:340, width:"100%", textAlign:"center", boxShadow:"0 8px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:32, marginBottom:10 }}>⚓</div>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:24, fontWeight:600, color:"#0B1626", marginBottom:16 }}>
              Skipper access
            </div>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              placeholder="••••"
              value={pinInput}
              onChange={e => { setPinInput(e.target.value); setPinError(false); }}
              onKeyDown={e => e.key === "Enter" && submitPin()}
              style={{ width:"100%", textAlign:"center", letterSpacing:"0.5em", fontSize:24, padding:"12px", borderRadius:8, border:`1px solid ${pinError ? "#c0392b" : "#D5CCBA"}`, background:"#EDE8DC", outline:"none", fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom: pinError ? 8 : 18 }}
            />
            {pinError && <div style={{ color:"#c0392b", fontSize:13, marginBottom:14, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Wrong PIN — try again</div>}
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button onClick={submitPin} style={{ background:"#0B1626", color:"#F4F1EA", border:"none", borderRadius:6, padding:"11px 24px", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Enter</button>
              <button onClick={() => { setPinPrompt(false); setPinInput(""); setPinError(false); }} style={{ background:"transparent", color:"#5A6A7A", border:"1px solid #D5CCBA", borderRadius:6, padding:"11px 20px", fontWeight:600, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
